import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { usePathname } from "next/navigation";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navbar, Spinner } from "react-bootstrap";
import Link from "next/link";

import type { WhoAmI } from "types/app";
import {
  AAThemeProvider,
  initColorMode,
  Sidebar,
  ToastBox,
  ToastManagerProvider,
} from "@bagel-web/components";
import { getIsActive, useNavConfig } from "utils/navConfig";
import {
  getQueryClientDefaultOptions,
  initUserContext,
  LocalStorageKeys,
  useLocalStorage,
} from "@bagel-web/common";
import { initializeFeatureFlags } from "utils/featureFlags";
import NavHeader from "components/NavHeader";
import { LayoutVisibilityProvider } from "contexts/LayoutVisibility";

const queryClient = new QueryClient(getQueryClientDefaultOptions());

TimeAgo.addDefaultLocale(en);

function ViewAsHeader({
  whoami,
  onLogOut,
}: {
  whoami: WhoAmI;
  onLogOut: () => void;
}) {
  const handleSignOut = () => {
    onLogOut();
    return false;
  };

  return (
    whoami.impersonator_email && (
      <Navbar className="bg-danger px-3" id="header">
        <b>
          You are viewing the dashboard as {whoami.organization.name}.
          <a href="#" onClick={handleSignOut} className="text-light ms-2">
            Sign out
          </a>
        </b>
      </Navbar>
    )
  );
}

type LayoutProps = {
  children: React.ReactNode;
  whoAmI: WhoAmI;
  setWhoAmI: (whoAmI: WhoAmI | null) => void;
};

// TODO: Refactor withAuthentication to be shared across Admin, Dashboard and Skunkworks.
// WhoAmI can be templated to make this generic.
const withAuthentication = (Layout: React.ComponentType<LayoutProps>) => {
  const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => {
    const [whoAmI, _setWhoAmI] = useState<WhoAmI | null>(null);

    const router = useRouter();

    const setWhoAmI = (currentUser: WhoAmI | null) => {
      initUserContext({
        userId: currentUser?.user.id || null,
        organization: currentUser?.organization.id || null,
        organizations: currentUser?.organizations ?? [],
        projectId: currentUser?.project?.id || null,
        projects: currentUser?.projects ?? [],
        roles: currentUser?.roles || [],
        permissions: currentUser?.permissions || [],
      });
      _setWhoAmI(currentUser);
    };

    const fetchCurrentSession = async () => {
      const response = await fetch("/api/auth/whoami");
      try {
        if (response.status === 200) {
          const jsonBody = await response.json();
          setWhoAmI(jsonBody);
          initializeFeatureFlags(queryClient);
        } else if (response.status === 401) {
          const jsonBody = await response.json();
          const redirectPath =
            typeof jsonBody?.redirect_path === "string" &&
            jsonBody.redirect_path.startsWith("/")
              ? jsonBody.redirect_path
              : "/auth/login";
          router.push(
            redirectPath +
              "?path=" +
              encodeURIComponent(
                window.location.pathname + window.location.search
              )
          );
        }
      } catch (err) {
        console.log(err);
      }
    };

    useEffect(() => {
      fetchCurrentSession();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      initColorMode();
    }, []);

    if (!whoAmI) {
      return (
        <div id="layout-loading">
          <Spinner />
        </div>
      );
    } else {
      return (
        <Layout whoAmI={whoAmI} setWhoAmI={setWhoAmI}>
          {children}
        </Layout>
      );
    }
  };
  return AuthenticatedLayout;
};

// Rendered inside the QueryClientProvider (unlike ClientLayout's own body),
// because useNavConfig soft-gates items on feature flags via
// useFeatureFlagValue, which reads the flag query.
const DashboardSidebar = ({
  whoAmI,
  pathname,
}: {
  whoAmI: WhoAmI;
  pathname: string;
}) => {
  const navConfig = useNavConfig({ whoAmI });
  return (
    <Sidebar
      config={navConfig}
      LinkComponent={Link}
      getIsActive={getIsActive(pathname)}
    />
  );
};

const ClientLayout = ({
  children,
  whoAmI,
  setWhoAmI,
}: Readonly<LayoutProps>) => {
  const router = useRouter();
  const pathname = usePathname();
  const localStorage = useLocalStorage<boolean>({ globalStorage: true });

  // Pages can toggle the surrounding chrome at runtime via the
  // LayoutVisibility context (see useHideLayout / useLayoutVisibility).
  const [hideLayout, setHideLayout] = useState(false);
  const layoutVisibility = useMemo(
    () => ({ hideLayout, setHideLayout }),
    [hideLayout]
  );

  const logOut = () => {
    router.push("/auth/logout");
  };

  const darkModeStoredPreference = localStorage.getItem(
    LocalStorageKeys.DARK_MODE
  );

  const handleUpdateDarkModeStoredPreference = (darkMode: boolean) => {
    localStorage.setItem(LocalStorageKeys.DARK_MODE, darkMode);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AAThemeProvider
        darkModeStoredPreference={darkModeStoredPreference}
        updateDarkModeStoredPreference={handleUpdateDarkModeStoredPreference}
      >
        <ToastManagerProvider>
          <LayoutVisibilityProvider value={layoutVisibility}>
            <div className="vh-100 d-flex flex-column align-items-stretch">
              <ViewAsHeader whoami={whoAmI} onLogOut={logOut} />
              {!hideLayout && (
                <NavHeader
                  whoami={whoAmI}
                  onLogOut={logOut}
                  onChangeWhoAmI={setWhoAmI}
                />
              )}
              <div
                className="d-flex flex-row align-items-stretch"
                style={{ flexGrow: 1, flexShrink: 1, overflow: "hidden" }}
              >
                {!hideLayout && (
                  <DashboardSidebar whoAmI={whoAmI} pathname={pathname} />
                )}
                <ToastBox />
                <main style={{ flexGrow: 1, overflow: "hidden" }}>
                  {children}
                </main>
              </div>
            </div>
          </LayoutVisibilityProvider>
        </ToastManagerProvider>
      </AAThemeProvider>
    </QueryClientProvider>
  );
};

export default withAuthentication(ClientLayout);
